import { useState, useEffect } from 'react';
import styled from 'styled-components';

import { SectionContainer } from 'components/DetailsView/Section';
import { ColumnDetails } from './components/ColumnDetails';
import { DeliveryProductSummary } from './components/storeProducts/StoreProductsSection';
import { ColumnGroupDetails } from './components/ColumnGroupDetails';
import { ColumnSetupSelection } from './components/ColumnSetupSelection';
import { FreefoodDetails } from './components/FreefoodDetails';
import { PermissionGate } from 'components/PermissionGate';

import {
    GetMicrostoreProductsQuery,
    MicrostoreColumnGroupSetupFragmentFragment,
    MicrostoreColumnSetupFragmentFragment,
    SiteInventoryFragmentFragment,
    SiteSetupFragmentFragment,
    SiteSetupState,
    TotadminPermission,
} from 'data/__generated__';

export const ColumnDetailsSection = ({
    deliveredStoreProductQuantities,
    deliveryProductSummary,
    products,
    siteInventory,
    siteSetup,
}: {
    deliveredStoreProductQuantities: Record<string, number>;
    deliveryProductSummary: DeliveryProductSummary;
    products: GetMicrostoreProductsQuery['getMicrostoreProducts'];
    siteInventory: SiteInventoryFragmentFragment | null;
    siteSetup: SiteSetupFragmentFragment;
}) => {
    const [selectedColumnGroupSetup, setSelectedColumnGroupSetup] =
        useState<MicrostoreColumnGroupSetupFragmentFragment | null>(null);
    const selectedColumnGroup =
        siteSetup.site.microstoreColumnGroups.find(
            (microstoreColumnGroup) => microstoreColumnGroup._id === selectedColumnGroupSetup?.microstoreColumnGroupId,
        ) ?? null;

    const [selectedColumnSetup, setSelectedColumnSetup] = useState<MicrostoreColumnSetupFragmentFragment | null>(null);

    const selectedColumn =
        selectedColumnGroup?.columns?.find((column) => column._id === selectedColumnSetup?.microstoreColumnId) ?? null;

    const selectedColumnInventory = siteInventory?.microstoreColumnInventories.find(
        ({ _id }) => _id === selectedColumnSetup?.microstoreColumnInventoryId,
    );

    useEffect(() => {
        if (selectedColumnSetup) {
            const updatedColumnSetup = siteSetup.microstoreColumnGroupSetups
                ?.flatMap((groupSetup) => groupSetup.microstoreColumnSetups || [])
                .find((columnSetup) => columnSetup._id === selectedColumnSetup._id);

            if (
                updatedColumnSetup &&
                updatedColumnSetup.postSiteSetupZeroInventoryProductIds !==
                    selectedColumnSetup.postSiteSetupZeroInventoryProductIds
            ) {
                setSelectedColumnSetup(updatedColumnSetup);
            }
        }
    }, [siteSetup, selectedColumnSetup]);

    return (
        <SectionContainer title="Détail du rangement" isInitiallyOpen>
            <>
                <WarningText>
                    Si la structure des colonnes a changé entre temps, les représentations sous forme de colonne seront
                    caduques
                </WarningText>
                <ColumnSetupSelection
                    site={siteSetup.site}
                    siteSetup={siteSetup}
                    selectedColumnSetup={selectedColumnSetup}
                    setSelectedColumnGroupSetup={setSelectedColumnGroupSetup}
                    setSelectedColumnSetup={setSelectedColumnSetup}
                />
                {selectedColumn && selectedColumnSetup ? (
                    <PermissionGate requiredPermissions={[TotadminPermission.SiteInventoriesRead]}>
                        {siteInventory && selectedColumnInventory ? (
                            <ColumnDetails
                                deliveryProductSummary={deliveryProductSummary}
                                inventoryDate={siteInventory.date}
                                products={products}
                                selectedColumn={selectedColumn}
                                selectedColumnInventory={selectedColumnInventory}
                                selectedColumnSetup={selectedColumnSetup}
                                site={siteSetup.site}
                                siteSetupDate={siteSetup.date}
                                siteSetupFinishedAt={
                                    siteSetup.stateHistory.find(({ state }) => state === SiteSetupState.Finished)
                                        ?.createdAt ?? null
                                }
                            />
                        ) : null}
                    </PermissionGate>
                ) : null}

                <SectionText>Photos du regroupement de colonne.</SectionText>
                {selectedColumnGroupSetup ? (
                    <ColumnGroupDetails selectedColumnGroupSetup={selectedColumnGroupSetup} />
                ) : null}
                <SectionText>Photos de la freefood.</SectionText>
                {siteSetup.freefoodSetup ? <FreefoodDetails freefoodSetup={siteSetup.freefoodSetup} /> : null}
                <SectionText>Problèmes rencontrés.</SectionText>
                {siteSetup.issues.productsIssues.map((productIssues) => {
                    const product = products.find(({ _id }) => _id === productIssues.productId);
                    return (
                        <ProducIssue key={productIssues.productId}>
                            <ProductName>
                                {product
                                    ? `${product?.name} - ${product?.brand} - ${product?.volume}`
                                    : productIssues.productId}
                            </ProductName>
                            <IssueComment>{productIssues.comment}</IssueComment>

                            {productIssues.brokenDuringDeliveryQuantity ? (
                                <Issue>
                                    Produit(s) cassé(s) durant le transport.:{' '}
                                    {productIssues.brokenDuringDeliveryQuantity}
                                </Issue>
                            ) : null}
                            {productIssues.brokenOnSiteBeforeInventoryQuantity ? (
                                <Issue>
                                    Produit(s) cassé(s) avant l'inventaire:{' '}
                                    {productIssues.brokenOnSiteBeforeInventoryQuantity}
                                </Issue>
                            ) : null}
                            {productIssues.brokenOnSiteAfterInventoryQuantity ? (
                                <Issue>
                                    Produit(s) cassé(s) après l'inventaire.:{' '}
                                    {productIssues.brokenOnSiteAfterInventoryQuantity}
                                </Issue>
                            ) : null}
                            {productIssues.expiredDeliveredQuantity ? (
                                <Issue>Produit(s) livré(s) périmé(s).: {productIssues.expiredDeliveredQuantity}</Issue>
                            ) : null}
                            {productIssues.expiredOnSiteBeforeInventoryQuantity ? (
                                <Issue>
                                    Produit(s) périmé(s) avant l'inventaire:{' '}
                                    {productIssues.expiredOnSiteBeforeInventoryQuantity}
                                </Issue>
                            ) : null}
                            {productIssues.expiredOnSiteAfterInventoryQuantity ? (
                                <Issue>
                                    Produit(s) périmé(s) après l'inventaire.:{' '}
                                    {productIssues.expiredOnSiteAfterInventoryQuantity}
                                </Issue>
                            ) : null}
                            {productIssues.notEnoughPlaceQuantity ? (
                                <Issue>Pas assez de place: {productIssues.notEnoughPlaceQuantity}</Issue>
                            ) : null}
                            {productIssues.otherBeforeInventoryQuantity ? (
                                <Issue>
                                    Problème(s) autre(s) avant l'inventaire:{' '}
                                    {productIssues.otherBeforeInventoryQuantity}
                                </Issue>
                            ) : null}
                            {productIssues.otherAfterInventoryOrDuringDeliveryQuantity ? (
                                <Issue>
                                    Problème(s) autre(s) après l'inventaire ou durant le transport.:{' '}
                                    {productIssues.otherAfterInventoryOrDuringDeliveryQuantity}
                                </Issue>
                            ) : null}
                            {productIssues.unexpectedOnSiteQuantity ? (
                                <Issue>
                                    Produit(s) non prévu(s) sur site: {productIssues.unexpectedOnSiteQuantity}
                                </Issue>
                            ) : null}
                            {productIssues.wrongQuantityDelivered !==
                            (deliveredStoreProductQuantities[productIssues.productId] ?? 0) ? (
                                <Issue>
                                    Quantité livrée : {productIssues.wrongQuantityDelivered} au lieu de{' '}
                                    {deliveredStoreProductQuantities[productIssues.productId] ?? 0}
                                </Issue>
                            ) : null}
                        </ProducIssue>
                    );
                })}
            </>
        </SectionContainer>
    );
};

const WarningText = styled.div`
    color: ${({ theme }) => theme.warningColor};
    font-weight: bold;
    margin-bottom: 10px;
`;

const SectionText = styled.div`
    font-weight: bold;
    font-size: 18px;
    margin-top: 20px;
`;

const ProducIssue = styled.div`
    margin-top: 10px;
`;
const ProductName = styled.div`
    font-weight: bold;
`;
const IssueComment = styled.div`
    margin-top: 5px;
`;
const Issue = styled.div`
    margin-top: 5px;
`;
